import React, { useState } from "react";
import { AlertCircle, Loader, Mail, CheckCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { showSuccessToast, showErrorToast } from "@/utils/toastUtils";
import { debugLogger } from '@/utils/debugLogger';

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBackToLogin }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isFormValid = () => {
    if (!email) {
      setError("Por favor, digite seu email");
      return false;
    }
    if (!isValidEmail(email)) {
      setError("Por favor, digite um email válido");
      return false;
    }
    return true;
  };

  const startResendCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendResetEmail = async (email: string) => {
    debugLogger.debug('Auth', 'Sending password reset email via edge function to:', email);
    
    const response = await fetch(
      'https://ohllmsaklzfbepnivvfc.supabase.co/functions/v1/send-password-recovery',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      debugLogger.error('Auth', 'Reset password error:', data.error);
      throw new Error(data.error || 'Erro ao enviar email de recuperação');
    }

    debugLogger.debug('Auth', 'Password reset email sent successfully via Resend');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!isFormValid()) return;
    
    setLoading(true);
    
    try {
      await sendResetEmail(email);

      setEmailSent(true);
      startResendCooldown();
      
      showSuccessToast({
        title: "Email enviado!",
        description: "Verifique sua caixa de entrada e clique no link para redefinir sua senha."
      });

    } catch (error: any) {
      let errorMsg = "Ocorreu um erro ao enviar o email.";
      if (error.message) {
        if (error.message.includes("rate limited")) {
          errorMsg = "Muitas tentativas. Tente novamente em alguns minutos.";
        } else if (error.message.includes("email not confirmed")) {
          errorMsg = "Este email não foi confirmado ainda.";
        } else {
          errorMsg = error.message;
        }
      }
      setError(errorMsg);
      showErrorToast({
        title: "Erro ao enviar email",
        description: errorMsg
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await sendResetEmail(email);

      startResendCooldown();
      showSuccessToast({
        title: "Email reenviado!",
        description: "Verifique sua caixa de entrada novamente."
      });

    } catch (error: any) {
      setError("Erro ao reenviar email. Tente novamente.");
      showErrorToast({
        title: "Erro ao reenviar",
        description: "Não foi possível reenviar o email. Tente novamente."
      });
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="space-y-6 text-center">
        <div className="mb-6">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Email Enviado!</h2>
          <p className="text-white/70 text-sm">
            Enviamos um link para redefinir sua senha para:
          </p>
          <p className="text-white font-medium mt-1">{email}</p>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 text-left">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-white/80">
              <p className="font-medium mb-2">Próximos passos:</p>
              <ol className="list-decimal list-inside space-y-1 text-white/70">
                <li>Verifique sua caixa de entrada (e spam)</li>
                <li>Clique no link no email recebido</li>
                <li>Você será redirecionado automaticamente</li>
                <li>Digite sua nova senha na página que abrir</li>
                <li>Faça login com a nova senha</li>
              </ol>
              <p className="mt-3 text-xs text-white/60">
                O link expira em 60 minutos por segurança.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            className="w-full bg-white/5 border-white/20 text-white hover:bg-white/10"
            onClick={handleResend}
            disabled={resendCooldown > 0 || loading}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <Loader className="animate-spin mr-2 h-4 w-4" />
                <span>Reenviando...</span>
              </div>
            ) : resendCooldown > 0 ? (
              <div className="flex items-center justify-center">
                <Clock className="mr-2 h-4 w-4" />
                <span>Reenviar em {resendCooldown}s</span>
              </div>
            ) : (
              "Reenviar Link"
            )}
          </Button>

          <button
            type="button"
            onClick={onBackToLogin}
            className="w-full text-sm text-luxury-red hover:text-luxury-red/90 transition"
          >
            Voltar para o login
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-white mb-2">Recuperar Senha</h2>
        <p className="text-white/70 text-sm">
          Digite seu email para receber um link de recuperação
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="reset-email" className="text-white">
          Email
        </Label>
        <div className="relative">
          <Input
            id="reset-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
            placeholder="seu@email.com"
            autoFocus
          />
          {email && !isValidEmail(email) && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-500 text-sm py-2 px-3 bg-red-500/10 rounded-md">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Button type="submit" className="w-full luxury-button" disabled={loading}>
        {loading ? (
          <div className="flex items-center justify-center">
            <Loader className="animate-spin mr-2 h-4 w-4" />
            <span>Enviando Link...</span>
          </div>
        ) : (
          "Enviar Link de Recuperação"
        )}
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={onBackToLogin}
          className="text-sm text-luxury-red hover:text-luxury-red/90 transition"
        >
          Voltar para o login
        </button>
      </div>
    </form>
  );
};

export default ForgotPasswordForm;
